/**
 * Pure helpers for the Knowledge use tab on the tenant admin AI usage screen —
 * the views, the columns each view shows, and the labels. Kept in lib/ so they
 * are unit-testable without rendering (console convention).
 *
 * Everything the tab shows is SERVED: names are resolved by the hub, rows
 * carry no ids (the console keys them by index), and times render absolute in
 * the tenant's zone — never relative.
 */

export type KnowledgeUseView = "entries" | "sessions" | "weeks";

export type Bucket = "recalled" | "re-derived" | "novel" | "no query";

export type KnowledgeUseSummary = {
  sessions: number;
  sql_sessions: number;
  people: number;
  queries: number;
  recalled: number;
  re_derived: number;
  novel: number;
  demanded_read_share: number | null;
  sessions_read_none: number;
  sessions_read_all: number;
  accepted_entries: number;
  entries_never_read: number;
  live_studies: number;
  universe_objects: number;
  objects_queried: number;
  objects_covered: number;
  session_gap_minutes: number;
  recorded_since: string | null;
};

export type KnowledgeUseWeek = {
  week_start: string;
  sql_sessions: number;
  demanded: number;
  demanded_read: number;
  share: number | null;
  recalled: number;
  re_derived: number;
  novel: number;
};

export type KnowledgeUseEntry = {
  name: string;
  description: string;
  demanded_sessions: number;
  read_sessions: number;
  skipped_sessions: number;
  served_in_search: number;
  hinted: number;
  reads: number;
  last_read_at: string | null;
};

export type KnowledgeUseSession = {
  person: string | null;
  app: string | null;
  started_at: string;
  ended_at: string;
  calls: number;
  queries: number;
  objects: number;
  demanded: number;
  demanded_read: number;
  share: number | null;
  studies_covering: number;
  studies_read: number;
  bucket: Bucket;
  skipped: string[];
};

export type KnowledgeUsePayload = {
  summary: KnowledgeUseSummary;
  weeks: KnowledgeUseWeek[];
  entries: KnowledgeUseEntry[];
  sessions: KnowledgeUseSession[];
  time_zone: string;
};

export const KNOWLEDGE_USE_VIEW_OPTIONS: ReadonlyArray<{ value: KnowledgeUseView; label: string }> = [
  { value: "entries", label: "Entries" },
  { value: "sessions", label: "Sessions" },
  { value: "weeks", label: "Weeks" },
];

export type KnowledgeUseColumn = { key: string; label: string; title?: string };

/** The columns each view shows, in order. */
export function knowledgeUseColumns(view: KnowledgeUseView): KnowledgeUseColumn[] {
  switch (view) {
    case "entries":
      return [
        { key: "name", label: "Entry" },
        { key: "demanded_sessions", label: "Demanded", title: "Sessions whose query receipts would have asked for this entry" },
        { key: "read_sessions", label: "Read", title: "Of those sessions, how many read it" },
        { key: "skipped_sessions", label: "Skipped", title: "Demanded and not read" },
        { key: "served_in_search", label: "In search results" },
        { key: "hinted", label: "Hinted" },
        { key: "reads", label: "Reads" },
        { key: "last_read_at", label: "Last read" },
      ];
    case "sessions":
      return [
        { key: "started_at", label: "Started" },
        { key: "person", label: "Person" },
        { key: "app", label: "App", title: "as reported by the connecting app" },
        { key: "queries", label: "Queries" },
        { key: "objects", label: "Objects" },
        { key: "demanded", label: "Demanded" },
        { key: "demanded_read", label: "Read" },
        { key: "share", label: "Share" },
        { key: "studies_read", label: "Studies read" },
        { key: "bucket", label: "Result" },
      ];
    case "weeks":
      return [
        { key: "week_start", label: "Week of" },
        { key: "sql_sessions", label: "Sessions" },
        { key: "demanded", label: "Demanded" },
        { key: "demanded_read", label: "Read" },
        { key: "share", label: "Share" },
        { key: "recalled", label: "Recalled" },
        { key: "re_derived", label: "Re-derived" },
        { key: "novel", label: "New ground" },
      ];
  }
}

/** A share as a whole percentage; `—` when nothing was demanded. */
export function formatShare(share: number | null | undefined): string {
  if (share == null || !Number.isFinite(share)) return "—";
  return `${Math.round(share * 100)}%`;
}

export function bucketLabel(bucket: Bucket): string {
  switch (bucket) {
    case "recalled":
      return "Recalled";
    case "re-derived":
      return "Re-derived";
    case "novel":
      return "New ground";
    case "no query":
      return "No query";
  }
}

/** The pill class a result renders with: re-derived is the one to notice. */
export function bucketPillClass(bucket: Bucket): string {
  switch (bucket) {
    case "recalled":
      return "pill pillOk";
    case "re-derived":
      return "pill pillBad";
    case "novel":
      return "pill pillWarn";
    case "no query":
      return "pill pillNeutral";
  }
}

/** The caption under the tables: the measurement epoch, as an absolute date in the tenant's zone. */
export function knowledgeUseCaption(iso: string | null, timeZone: string, locale?: string): string | null {
  if (!iso) return null;
  let zone = timeZone || "UTC";
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: zone });
  } catch {
    zone = "UTC";
  }
  const date = new Date(iso).toLocaleDateString(locale, { timeZone: zone, year: "numeric", month: "long", day: "numeric" });
  return `Door sessions recorded since ${date}. Demanded = the entries a query receipt names for what the SQL read, five at most per query.`;
}
